// src/pages/Pedidos.jsx
import Cargando from "../components/Cargando";
import React, { useState, useMemo, useEffect } from "react";
import { usePedidos } from "../hooks/usePedidos";
import { useClientes } from "../hooks/useClientes";
import { useCotizaciones } from "../hooks/useCotizaciones";
import { useProveedores } from "../hooks/useProveedores";
import { useSearchParams } from "react-router-dom";
import { doc, updateDoc, deleteDoc } from "firebase/firestore";
import { db } from "../firebase";
import toast from "react-hot-toast";
import * as XLSX from "xlsx";

const ESTADO_STYLE = {
  "Solicitud recibida": { bg: "#E6F1FB", color: "#185FA5", border: "#185FA5" },
  "En preparación":     { bg: "#EEEDFE", color: "#534AB7", border: "#534AB7" },
  "En tránsito":        { bg: "#FAEEDA", color: "#854F0B", border: "#F97316" },
  "En reparto":         { bg: "#FEF3C7", color: "#92400E", border: "#D97706" },
  "Entregado":          { bg: "#E1F5EE", color: "#0F6E56", border: "#1D9E75" },
  "Cancelado":          { bg: "#FCEBEB", color: "#A32D2D", border: "#DC2626" },
};

const INICIAL = {
  numero: "", cotizacionId: "", clienteId: "", clienteNombre: "", clienteEmpresa: "",
  transportistaId: "", materiales: "", valor: "",
  pesoToneladas: "", fechaEntrega: "", direccionEntrega: "", notas: "", pagado: false,
};

const lbl = { display: "block", fontSize: 11, fontWeight: 600, color: "#475569", marginBottom: 5 };
const inp = { width: "100%", padding: "9px 12px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 13, color: "#1e293b", outline: "none", background: "#fff", fontFamily: "inherit", boxSizing: "border-box" };
const sel = { ...inp, cursor: "pointer" };

export default function Pedidos() {
  const { pedidos, loading, crearPedido, actualizarEstado } = usePedidos();
  const { clientes }                    = useClientes();
  const { cotizaciones }                = useCotizaciones();
  const { proveedores: transportistas } = useProveedores("transporte");
  const [searchParams, setSearchParams] = useSearchParams();

  // Pre-rellenar formulario cuando viene desde una cotización cerrada
  useEffect(() => {
    const cotId = searchParams.get("cotId");
    if (!cotId) return;
    setForm(f => ({
      ...INICIAL,
      cotizacionId:   cotId,
      clienteId:      searchParams.get("clienteId")      || "",
      clienteNombre:  decodeURIComponent(searchParams.get("clienteNombre")  || ""),
      clienteEmpresa: decodeURIComponent(searchParams.get("clienteEmpresa") || ""),
      valor:          searchParams.get("valor")           || "",
      notas:          decodeURIComponent(searchParams.get("notas")          || ""),
    }));
    setMostrarForm(true);
    setSearchParams({}, { replace: true }); // limpiar URL
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [form,          setForm]          = useState(INICIAL);
  const [mostrarForm,   setMostrarForm]   = useState(false);
  const [modoEditar,    setModoEditar]    = useState(false);
  const [editPedidoId,  setEditPedidoId]  = useState(null);
  const [enviando,      setEnviando]      = useState(false);
  const [filtroEstado,  setFiltroEstado]  = useState("todos");
  const [soloSinPagar,  setSoloSinPagar]  = useState(false);
  const [confirmDel,    setConfirmDel]    = useState(null);
  const [busqueda,      setBusqueda]      = useState("");
  const [pagina,        setPagina]        = useState(1);
  const [editNumId,     setEditNumId]     = useState(null);
  const [editNumVal,    setEditNumVal]    = useState("");

  const ESTADOS_PEDIDO = ["Solicitud recibida","En preparación","En tránsito","En reparto","Entregado","Cancelado"];

  const kpisLocal = useMemo(() => {
    const porEstado = {}; const porEstadoValor = {};
    ESTADOS_PEDIDO.forEach(e => { porEstado[e] = 0; porEstadoValor[e] = 0; });
    pedidos.forEach(p => {
      if (p.estado in porEstado) { porEstado[p.estado]++; porEstadoValor[p.estado] += Number(p.valor) || 0; }
    });
    const valorActivo  = ESTADOS_PEDIDO.filter(e => !["Entregado","Cancelado"].includes(e))
      .reduce((s, e) => s + porEstadoValor[e], 0);
    const saldoTotal   = pedidos.filter(p => !p.pagado && p.estado !== "Cancelado")
      .reduce((s, p) => s + (Number(p.valor) || 0), 0);
    const sinPagarCnt  = pedidos.filter(p => !p.pagado && p.estado !== "Cancelado").length;
    const adelantoTotal = pedidos.reduce((s, p) => s + (Number(p.totalPagado) || 0), 0);
    const margenTotal  = pedidos.reduce((s, p) => s + (Number(p.margenNeto) || 0), 0);
    const totalValor   = pedidos.reduce((s, p) => s + (Number(p.valor) || 0), 0);
    return { porEstado, porEstadoValor, valorActivo, saldoTotal, sinPagarCnt, adelantoTotal, margenTotal, totalValor };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pedidos]);

  const pedidosFiltrados = useMemo(() => {
    let lista = pedidos.filter(p => {
      if (filtroEstado !== "todos") return p.estado === filtroEstado;
      return true;
    });
    if (soloSinPagar) lista = lista.filter(p => !p.pagado && p.estado !== "Cancelado");
    if (busqueda) {
      const b = busqueda.toLowerCase();
      lista = lista.filter(p =>
        p.clienteNombre?.toLowerCase().includes(b) ||
        p.clienteEmpresa?.toLowerCase().includes(b) ||
        p.materiales?.toLowerCase().includes(b) ||
        p.numero?.toLowerCase().includes(b)
      );
    }
    // Ordenar por número de orden: ORD-YYYY-XXXX desc (año y número numérico)
    lista = [...lista].sort((a, b) => {
      const parse = n => {
        const m = (n || "").match(/ORD-(\d{4})-(\d+)/);
        if (m) return { year: parseInt(m[1]), num: parseInt(m[2]) };
        return { year: 0, num: parseInt(n) || 0 };
      };
      const pa = parse(a.numero || a.codigoSeguimiento);
      const pb = parse(b.numero || b.codigoSeguimiento);
      if (pb.year !== pa.year) return pb.year - pa.year;
      return pb.num - pa.num;
    });
    return lista;
  }, [pedidos, filtroEstado, soloSinPagar, busqueda]);

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const handleCotizacionChange = (cotId) => {
    const cot = cotizaciones.find(c => c.id === cotId);
    if (cot) {
      setForm(f => ({ ...f, cotizacionId: cotId, clienteId: cot.clienteId || "", clienteNombre: cot.clienteNombre || "", clienteEmpresa: cot.clienteEmpresa || "", valor: cot.valor || "" }));
    } else {
      setForm(f => ({ ...f, cotizacionId: "", clienteId: "", clienteNombre: "", clienteEmpresa: "" }));
    }
  };

  const abrirNuevo  = () => { setForm(INICIAL); setModoEditar(false); setEditPedidoId(null); setMostrarForm(true); };
  const abrirEditar = (p) => {
    setForm({ numero: p.numero || "", cotizacionId: p.cotizacionId || "", clienteId: p.clienteId || "", clienteNombre: p.clienteNombre || "", clienteEmpresa: p.clienteEmpresa || "", transportistaId: p.transportistaId || "", materiales: p.materiales || "", valor: p.valor || "", pesoToneladas: p.pesoToneladas || "", fechaEntrega: p.fechaEntrega || "", direccionEntrega: p.direccionEntrega || "", notas: p.notas || "", pagado: p.pagado || false });
    setModoEditar(true); setEditPedidoId(p.id); setMostrarForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setEnviando(true);
    if (modoEditar && editPedidoId) {
      await updateDoc(doc(db, "pedidos", editPedidoId), { ...form, valor: Number(form.valor) || 0 });
      toast.success("✅ Pedido actualizado");
    } else {
      const cliente      = clientes.find(c => c.id === form.clienteId);
      const transportista = transportistas.find(t => t.id === form.transportistaId);
      await crearPedido(form, cliente, transportista);
    }
    setForm(INICIAL); setMostrarForm(false); setModoEditar(false); setEditPedidoId(null); setEnviando(false);
  };

  const eliminarPedido = async (id) => {
    try { await deleteDoc(doc(db, "pedidos", id)); toast.success("🗑 Pedido eliminado"); setConfirmDel(null); }
    catch (e) { toast.error("Error al eliminar"); }
  };

  const cambiarEstado = async (pedido, nuevoEstado) => {
    const cliente       = clientes.find(c => c.id === pedido.clienteId);
    const transportista = transportistas.find(t => t.id === pedido.transportistaId);
    await actualizarEstado(pedido.id, nuevoEstado, pedido, cliente, transportista);
  };

  const togglePagado = async (pedido) => {
    await updateDoc(doc(db, "pedidos", pedido.id), { pagado: !pedido.pagado });
    toast.success(pedido.pagado ? "Marcado como pendiente" : "✅ Marcado como pagado");
  };

  const guardarNumero = async (id) => { await updateDoc(doc(db, "pedidos", id), { numero: editNumVal }); setEditNumId(null); };

  const exportarExcel = () => {
    const data = pedidosFiltrados.map(p => {
      const transp = transportistas.find(t => t.id === p.transportistaId);
      return {
        "N° Pedido":          p.numero || "",
        "Código seguimiento": p.codigoSeguimiento || "",
        "Cliente":            p.clienteNombre || "",
        "Empresa":            p.clienteEmpresa || "",
        "Materiales":         p.materiales || "",
        "Valor (S/)":         Number(p.valor) || 0,
        "Adelanto (S/)":      Number(p.totalPagado) || 0,
        "Saldo pendiente (S/)": Number(p.saldoPendiente) || 0,
        "Margen neto (S/)":   Number(p.margenNeto) || 0,
        "Peso (ton)":         p.pesoToneladas || "",
        "Estado entrega":     p.estado || "",
        "Estado pedido":      p.estadoPedido || "",
        "Confirmado":         p.pagado ? "Sí" : "No",
        "Transportista CRM":  transp?.nombre || "",
        "Transportista buyer": p.nombreTransporte || "",
        "Fecha entrega":      p.fechaEntrega || "",
        "Dirección":          p.direccionEntrega || "",
        "Notas CRM":          p.notas || "",
        "Notas buyer":        p.notasBuyer || "",
      };
    });
    const ws = XLSX.utils.json_to_sheet(data);
    ws["!cols"] = Array(19).fill({ wch: 20 });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Pedidos");
    XLSX.writeFile(wb, `Pedidos_${new Date().toLocaleDateString("es-PE").replace(/\//g, "-")}.xlsx`);
  };

  if (loading) return <Cargando />;

  const cotizacionesCerradas = cotizaciones.filter(c => ["Ganada", "Cerrada"].includes(c.estado));
  const POR_PAGINA      = 25;
  const totalPaginasPed = Math.max(1, Math.ceil(pedidosFiltrados.length / POR_PAGINA));
  const paginaValida    = Math.min(pagina, totalPaginasPed);
  const paginados       = pedidosFiltrados.slice((paginaValida - 1) * POR_PAGINA, paginaValida * POR_PAGINA);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: "#1e293b" }}>Pedidos</h1>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={exportarExcel} style={{ background: "#fff", color: "#1D9E75", border: "1.5px solid #1D9E75", fontSize: 13, fontWeight: 600, padding: "8px 14px", borderRadius: 8, cursor: "pointer" }}>
            ↓ Excel
          </button>
          <button onClick={abrirNuevo} style={{ background: "#1E3A5F", color: "#fff", border: "none", borderRadius: 8, padding: "8px 18px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
            + Nuevo pedido
          </button>
        </div>
      </div>

      {/* Micro-interacciones Emil Kowalski */}
      <style>{`
        .ped-chip { transition: background 150ms cubic-bezier(0.23,1,0.32,1), box-shadow 150ms cubic-bezier(0.23,1,0.32,1), transform 100ms cubic-bezier(0.23,1,0.32,1); }
        .ped-chip:active { transform: scale(0.96) !important; }
        .ped-chip:hover  { filter: brightness(0.96); }
        .ped-kpi { transition: box-shadow 200ms cubic-bezier(0.23,1,0.32,1), transform 200ms cubic-bezier(0.23,1,0.32,1); }
        .ped-kpi:hover { box-shadow: 0 6px 20px rgba(30,58,95,0.10); transform: translateY(-1px); }
        .ped-toggle { transition: background 140ms cubic-bezier(0.23,1,0.32,1), color 140ms cubic-bezier(0.23,1,0.32,1); }
        .ped-toggle:active { transform: scale(0.97); }
      `}</style>

      {/* Fila 1 — Estado chips: filtro + KPI combinado */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 8 }}>
        {/* Chip Todos */}
        {(() => {
          const activo = filtroEstado === "todos";
          return (
            <button className="ped-chip" onClick={() => { setFiltroEstado("todos"); setPagina(1); }} style={{
              background: activo ? "#1E3A5F" : "#fff",
              color: activo ? "#fff" : "#475569",
              border: `1.5px solid ${activo ? "#1E3A5F" : "#e2e8f0"}`,
              borderRadius: 12, padding: "12px 6px", cursor: "pointer", textAlign: "center",
              boxShadow: activo ? "0 2px 10px rgba(30,58,95,0.28)" : "none",
            }}>
              <div style={{ fontSize: 9, fontWeight: 700, opacity: 0.75, marginBottom: 3, textTransform: "uppercase", letterSpacing: "0.05em" }}>Todos</div>
              <div style={{ fontSize: 20, fontWeight: 800, lineHeight: 1 }}>{pedidos.length}</div>
              <div style={{ fontSize: 9, marginTop: 4, opacity: 0.7 }}>S/ {kpisLocal.totalValor.toLocaleString("es-PE")}</div>
            </button>
          );
        })()}
        {/* Chips por estado */}
        {ESTADOS_PEDIDO.map(e => {
          const s = ESTADO_STYLE[e];
          const activo = filtroEstado === e;
          return (
            <button key={e} className="ped-chip" onClick={() => { setFiltroEstado(e); setPagina(1); }} style={{
              background: activo ? s.color : s.bg,
              color: activo ? "#fff" : s.color,
              border: `1.5px solid ${activo ? s.color : "transparent"}`,
              borderRadius: 12, padding: "12px 6px", cursor: "pointer", textAlign: "center",
              boxShadow: activo ? `0 2px 10px ${s.color}44` : "none",
            }}>
              <div style={{ fontSize: 9, fontWeight: 700, opacity: activo ? 0.9 : 0.75, marginBottom: 3, textTransform: "uppercase", letterSpacing: "0.04em", lineHeight: 1.2 }}>{e}</div>
              <div style={{ fontSize: 20, fontWeight: 800, lineHeight: 1, marginTop: 2 }}>{kpisLocal.porEstado[e] || 0}</div>
              <div style={{ fontSize: 9, marginTop: 4, opacity: activo ? 0.9 : 0.7 }}>S/ {(kpisLocal.porEstadoValor[e] || 0).toLocaleString("es-PE")}</div>
            </button>
          );
        })}
      </div>

      {/* Fila 2 — Métricas de performance */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10 }}>
        {[
          { label: "Valor activo",     valor: `S/ ${kpisLocal.valorActivo.toLocaleString("es-PE")}`,    sub: `${(kpisLocal.porEstado["Solicitud recibida"]||0)+(kpisLocal.porEstado["En preparación"]||0)+(kpisLocal.porEstado["En tránsito"]||0)+(kpisLocal.porEstado["En reparto"]||0)} pedidos en proceso`, color: "#1E3A5F" },
          { label: "Saldo por cobrar", valor: `S/ ${kpisLocal.saldoTotal.toLocaleString("es-PE")}`,     sub: kpisLocal.sinPagarCnt > 0 ? `${kpisLocal.sinPagarCnt} pedidos con saldo` : "Todo cobrado", color: kpisLocal.sinPagarCnt > 0 ? "#DC2626" : "#1D9E75" },
          { label: "Adelantos recibidos", valor: `S/ ${kpisLocal.adelantoTotal.toLocaleString("es-PE")}`, sub: "pagos parciales registrados", color: "#0891B2" },
          { label: "Margen neto",      valor: `S/ ${kpisLocal.margenTotal.toLocaleString("es-PE")}`,    sub: "acumulado de ventas buyer", color: "#8B5CF6" },
        ].map(k => (
          <div key={k.label} className="ped-kpi" style={{ background: "#fff", border: "0.5px solid #e8ecf3", borderTop: `3px solid ${k.color}`, borderRadius: 12, padding: "14px 16px" }}>
            <div style={{ fontSize: 10, color: "#64748b", fontWeight: 500, marginBottom: 4 }}>{k.label}</div>
            <div style={{ fontSize: 18, fontWeight: 800, color: "#1e293b", letterSpacing: "-0.4px" }}>{k.valor}</div>
            <div style={{ fontSize: 10, marginTop: 4, color: k.color, fontWeight: 500 }}>{k.sub}</div>
          </div>
        ))}
      </div>

      {/* Formulario nuevo / editar */}
      {mostrarForm && (
        <div className="form-enter" style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 20 }}>
          <p style={{ fontSize: 13, fontWeight: 700, color: "#3A3C7A", marginBottom: 14, textTransform: "uppercase", letterSpacing: "0.06em" }}>
            {modoEditar ? "✏ Editar pedido" : "Nuevo pedido"}
          </p>
          <form onSubmit={handleSubmit}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
              <div>
                <label style={lbl}>N° Pedido</label>
                <input name="numero" value={form.numero} onChange={handleChange} placeholder="Ej: PED-2026-001" style={inp} />
              </div>
              <div>
                <label style={lbl}>Cotización vinculada</label>
                <select name="cotizacionId" value={form.cotizacionId} onChange={e => handleCotizacionChange(e.target.value)} style={sel}>
                  <option value="">— Sin cotización —</option>
                  {cotizacionesCerradas.map(c => <option key={c.id} value={c.id}>{c.numeroCot ? `${c.numeroCot} · ` : ""}{c.clienteNombre} — {c.proyecto || "Sin proyecto"}</option>)}
                </select>
              </div>
              <div style={{ gridColumn: "1/-1" }}>
                <label style={lbl}>Cliente *</label>
                <select name="clienteId" value={form.clienteId} onChange={handleChange} required style={sel}>
                  <option value="">— Selecciona cliente —</option>
                  {clientes.map(c => <option key={c.id} value={c.id}>{c.nombre} — {c.empresa}</option>)}
                </select>
                {form.clienteNombre && <div style={{ fontSize: 11, color: "#1D9E75", marginTop: 4 }}>✓ {form.clienteNombre}</div>}
              </div>
              <div>
                <label style={lbl}>Materiales *</label>
                <input name="materiales" value={form.materiales} onChange={handleChange} required placeholder="Ej: 500 bolsas cemento Portland" style={inp} />
              </div>
              <div>
                <label style={lbl}>Valor (S/) *</label>
                <input name="valor" type="number" value={form.valor} onChange={handleChange} required placeholder="Ej: 12400" style={inp} />
              </div>
              <div>
                <label style={lbl}>Peso aprox. (toneladas)</label>
                <input name="pesoToneladas" value={form.pesoToneladas} onChange={handleChange} placeholder="Ej: 22.5" style={inp} />
              </div>
              <div>
                <label style={lbl}>Fecha de entrega *</label>
                <input name="fechaEntrega" type="date" value={form.fechaEntrega} onChange={handleChange} required style={inp} />
              </div>
              <div>
                <label style={lbl}>Transportista</label>
                <select name="transportistaId" value={form.transportistaId} onChange={handleChange} style={sel}>
                  <option value="">— Por asignar —</option>
                  {transportistas.map(t => <option key={t.id} value={t.id}>{t.nombre} — {t.placa}</option>)}
                </select>
              </div>
              <div>
                <label style={lbl}>Dirección de entrega</label>
                <input name="direccionEntrega" value={form.direccionEntrega} onChange={handleChange} placeholder="Ej: Av. Túpac Amaru 1240" style={inp} />
              </div>
              <div style={{ gridColumn: "1/-1" }}>
                <label style={lbl}>Notas internas</label>
                <textarea name="notas" value={form.notas} onChange={handleChange} rows={2} placeholder="Instrucciones especiales..." style={{ ...inp, resize: "vertical" }} />
              </div>
            </div>
            <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
              <button type="submit" disabled={enviando} style={{ flex: 1, padding: "10px 0", background: "#1E3A5F", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                {enviando ? "Guardando..." : modoEditar ? "Guardar cambios" : "✅ Crear pedido"}
              </button>
              <button type="button" onClick={() => { setMostrarForm(false); setModoEditar(false); }} style={{ padding: "10px 18px", border: "1px solid #e2e8f0", borderRadius: 8, background: "#fff", fontSize: 13, cursor: "pointer", color: "#475569" }}>
                Cancelar
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Filtros — búsqueda + sin pagar */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        <input
          placeholder="Buscar cliente, materiales, N° pedido..."
          value={busqueda} onChange={e => setBusqueda(e.target.value)}
          style={{ flex: 1, minWidth: 200, padding: "7px 12px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 13, outline: "none" }}
        />
        <button className="ped-toggle" onClick={() => { setSoloSinPagar(v => !v); setPagina(1); }} style={{
          padding: "6px 14px", borderRadius: 8, fontSize: 11, fontWeight: 600, border: "none", cursor: "pointer",
          background: soloSinPagar ? "#DC2626" : "#FEF9C3",
          color:      soloSinPagar ? "#fff"    : "#854F0B",
        }}>
          {soloSinPagar ? "✕ Sin pagar" : `Sin pagar${kpisLocal.sinPagarCnt > 0 ? ` (${kpisLocal.sinPagarCnt})` : ""}`}
        </button>
        {(busqueda || soloSinPagar || filtroEstado !== "todos") && (
          <button onClick={() => { setBusqueda(""); setSoloSinPagar(false); setFiltroEstado("todos"); setPagina(1); }}
            style={{ padding: "5px 10px", border: "none", borderRadius: 8, fontSize: 11, color: "#DC2626", background: "#FCEBEB", cursor: "pointer", fontWeight: 600 }}>
            × Limpiar filtros
          </button>
        )}
      </div>

      {/* Tabla */}
      <div style={{ background: "#fff", border: "0.5px solid #e2e8f0", borderRadius: 12, overflow: "hidden" }}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr style={{ background: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
                {["N° Pedido", "Cotización", "Cliente", "Materiales", "Valor (S/)", "Cobro", "Transporte", "Entrega", "Estado", "Acciones"].map(h => (
                  <th key={h} style={{ textAlign: "left", padding: "8px 12px", fontSize: 10, fontWeight: 600, color: "#94a3b8", whiteSpace: "nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {paginados.length === 0 ? (
                <tr><td colSpan={10} style={{ textAlign: "center", padding: 40, color: "#94a3b8" }}>No hay pedidos en este filtro</td></tr>
              ) : paginados.map(p => {
                const transp   = transportistas.find(t => t.id === p.transportistaId);
                const cot      = cotizaciones.find(c => c.id === p.cotizacionId);
                const hoyD2=new Date(); const hoy=`${hoyD2.getFullYear()}-${String(hoyD2.getMonth()+1).padStart(2,"0")}-${String(hoyD2.getDate()).padStart(2,"0")}`;
                const venceHoy = p.fechaEntrega === hoy && p.estado !== "Entregado";
                const es       = ESTADO_STYLE[p.estado] || ESTADO_STYLE["Solicitud recibida"];

                return (
                  <tr key={p.id} style={{ borderBottom: "0.5px solid #f1f5f9" }}>

                    {/* N° Pedido editable */}
                    <td style={{ padding: "10px 12px" }}>
                      {editNumId === p.id ? (
                        <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                          <input value={editNumVal} onChange={e => setEditNumVal(e.target.value)}
                            style={{ width: 110, padding: "3px 7px", border: "1px solid #3A3C7A", borderRadius: 5, fontSize: 11 }}
                            autoFocus onKeyDown={e => { if (e.key === "Enter") guardarNumero(p.id); if (e.key === "Escape") setEditNumId(null); }} />
                          <button onClick={() => guardarNumero(p.id)} style={{ fontSize: 10, padding: "2px 6px", borderRadius: 4, background: "#3A3C7A", color: "#fff", border: "none", cursor: "pointer" }}>✓</button>
                          <button onClick={() => setEditNumId(null)} style={{ fontSize: 10, padding: "2px 5px", borderRadius: 4, background: "#f1f5f9", color: "#475569", border: "none", cursor: "pointer" }}>✕</button>
                        </div>
                      ) : (
                        <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                          <span style={{ fontWeight: 700, color: "#1E3A5F" }}>
                            {p.numero
                              ? `#${p.numero}`
                              : p.codigoSeguimiento
                              ? p.codigoSeguimiento
                              : `#PED-${String(p.id).slice(-4).toUpperCase()}`}
                          </span>
                          <button onClick={() => { setEditNumId(p.id); setEditNumVal(p.numero || ""); }}
                            style={{ fontSize: 9, padding: "1px 5px", borderRadius: 4, background: "#f1f5f9", color: "#64748b", border: "none", cursor: "pointer" }}>✏</button>
                        </div>
                      )}
                    </td>

                    <td style={{ padding: "10px 12px" }}>
                      {cot ? <span style={{ fontSize: 10, fontWeight: 700, color: "#3A3C7A", background: "#EEF2FF", padding: "2px 7px", borderRadius: 5 }}>{cot.numeroCot || "COT"}</span>
                           : <span style={{ fontSize: 10, color: "#94a3b8" }}>—</span>}
                    </td>

                    <td style={{ padding: "10px 12px" }}>
                      <div style={{ fontWeight: 500, color: "#1e293b" }}>{p.clienteNombre || "—"}</div>
                      <div style={{ fontSize: 10, color: "#94a3b8" }}>{p.clienteEmpresa}</div>
                    </td>

                    <td style={{ padding: "10px 12px", color: "#475569", maxWidth: 140, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {p.materiales}
                    </td>

                    {/* Valor + margen */}
                    <td style={{ padding: "10px 12px", fontWeight: 700, color: "#1e293b" }}>
                      S/ {Number(p.valor || 0).toLocaleString("es-PE")}
                      {p.margenNeto != null && (
                        <div style={{ fontSize: 10, color: "#1D9E75", fontWeight: 600, marginTop: 2 }}>
                          Margen: S/ {Number(p.margenNeto).toLocaleString("es-PE")}
                        </div>
                      )}
                      {p.codigoSeguimiento && (
                        <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 1 }}>{p.codigoSeguimiento}</div>
                      )}
                    </td>

                    {/* Cobro: adelanto + saldo + toggle */}
                    <td style={{ padding: "10px 12px", minWidth: 120 }}>
                      {p.saldoPendiente != null ? (
                        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                          {Number(p.totalPagado) > 0 && (
                            <div style={{ fontSize: 10, fontWeight: 600, color: "#0891B2" }}>
                              ↑ S/ {Number(p.totalPagado).toLocaleString("es-PE")} pagado
                            </div>
                          )}
                          <div style={{ fontSize: 10, fontWeight: 700, color: (p.pagado || Number(p.saldoPendiente) === 0) ? "#0F6E56" : "#DC2626" }}>
                            {Number(p.saldoPendiente) === 0
                              ? "✓ Sin saldo"
                              : p.pagado
                              ? `✓ S/ ${Number(p.saldoPendiente).toLocaleString("es-PE")} cobrado`
                              : `↓ S/ ${Number(p.saldoPendiente).toLocaleString("es-PE")} saldo`}
                          </div>
                          <button onClick={() => togglePagado(p)} style={{
                            fontSize: 9, fontWeight: 600, padding: "2px 7px", borderRadius: 5, border: "none", cursor: "pointer", marginTop: 1,
                            background: p.pagado ? "#E1F5EE" : "#F1F5F9",
                            color:      p.pagado ? "#0F6E56" : "#64748b",
                          }}>
                            {p.pagado ? "✓ Confirmado" : "Confirmar"}
                          </button>
                        </div>
                      ) : (
                        <button onClick={() => togglePagado(p)} style={{
                          fontSize: 10, fontWeight: 600, padding: "3px 8px", borderRadius: 6, border: "none", cursor: "pointer",
                          background: p.pagado ? "#E1F5EE" : "#FEF9C3",
                          color:      p.pagado ? "#0F6E56" : "#854F0B",
                        }}>
                          {p.pagado ? "✓ Pagado" : "Pendiente"}
                        </button>
                      )}
                    </td>

                    {/* Transporte: buyer nombreTransporte o CRM transportista */}
                    <td style={{ padding: "10px 12px", fontSize: 11 }}>
                      {transp?.nombre
                        ? <span style={{ color: "#475569" }}>{transp.nombre}</span>
                        : p.nombreTransporte
                        ? <span style={{ color: "#475569" }}>{p.nombreTransporte}</span>
                        : <span style={{ color: "#94a3b8" }}>Por asignar</span>}
                    </td>

                    <td style={{ padding: "10px 12px", fontWeight: 500, color: venceHoy ? "#DC2626" : "#475569" }}>
                      {p.fechaEntrega ? new Date(p.fechaEntrega).toLocaleDateString("es-PE") : "—"}
                      {venceHoy && <span style={{ fontSize: 10, display: "block", color: "#DC2626" }}>⚠ Hoy</span>}
                    </td>

                    <td style={{ padding: "10px 12px" }}>
                      <span style={{ fontSize: 10, fontWeight: 600, padding: "3px 8px", borderRadius: 8, background: es.bg, color: es.color }}>
                        {p.estado}
                      </span>
                    </td>

                    <td style={{ padding: "10px 12px" }}>
                      <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                        {p.estado === "Solicitud recibida" && <button onClick={() => cambiarEstado(p, "En preparación")} style={{ fontSize: 10, fontWeight: 600, padding: "4px 8px", borderRadius: 6, border: "none", cursor: "pointer", background: "#EEEDFE", color: "#534AB7" }}>📦 Preparar</button>}
                        {p.estado === "En preparación"    && <button onClick={() => cambiarEstado(p, "En tránsito")}    style={{ fontSize: 10, fontWeight: 600, padding: "4px 8px", borderRadius: 6, border: "none", cursor: "pointer", background: "#FAEEDA", color: "#854F0B" }}>🚚 Despachar</button>}
                        {p.estado === "En tránsito"       && <button onClick={() => cambiarEstado(p, "En reparto")}     style={{ fontSize: 10, fontWeight: 600, padding: "4px 8px", borderRadius: 6, border: "none", cursor: "pointer", background: "#FEF3C7", color: "#92400E" }}>📍 En reparto</button>}
                        {p.estado === "En reparto"        && <button onClick={() => cambiarEstado(p, "Entregado")}      style={{ fontSize: 10, fontWeight: 600, padding: "4px 8px", borderRadius: 6, border: "none", cursor: "pointer", background: "#E1F5EE", color: "#0F6E56" }}>✅ Entregado</button>}
                        <button onClick={() => abrirEditar(p)} style={{ fontSize: 10, fontWeight: 600, padding: "4px 8px", borderRadius: 6, border: "none", cursor: "pointer", background: "#E6F1FB", color: "#185FA5" }}>Editar</button>
                        {confirmDel === p.id ? (
                          <>
                            <button onClick={() => eliminarPedido(p.id)} style={{ fontSize: 10, fontWeight: 600, padding: "4px 8px", borderRadius: 6, border: "none", cursor: "pointer", background: "#DC2626", color: "#fff" }}>Sí</button>
                            <button onClick={() => setConfirmDel(null)} style={{ fontSize: 10, padding: "4px 7px", borderRadius: 6, border: "none", cursor: "pointer", background: "#f1f5f9", color: "#475569" }}>No</button>
                          </>
                        ) : (
                          <button onClick={() => setConfirmDel(p.id)} style={{ fontSize: 10, fontWeight: 600, padding: "4px 8px", borderRadius: 6, border: "none", cursor: "pointer", background: "#FCEBEB", color: "#A32D2D" }}>Del</button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <BarraPaginas total={pedidosFiltrados.length} porPagina={POR_PAGINA} pagina={paginaValida} onChange={setPagina} />
      </div>
    </div>
  );
}

function BarraPaginas({ total, porPagina, pagina, onChange }) {
  if (!total || total <= porPagina) return null;
  const totalPags = Math.ceil(total / porPagina);
  const desde = (pagina - 1) * porPagina + 1;
  const hasta  = Math.min(pagina * porPagina, total);
  const nums = [];
  for (let i = 1; i <= totalPags; i++) {
    if (i === 1 || i === totalPags || (i >= pagina - 1 && i <= pagina + 1)) nums.push(i);
    else if (nums[nums.length - 1] !== "...") nums.push("...");
  }
  const btnBase = { padding: "5px 12px", borderRadius: 6, border: "1px solid #e2e8f0", background: "#fff", fontSize: 12, fontWeight: 500, cursor: "pointer" };
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", borderTop: "1px solid #f1f5f9" }}>
      <span style={{ fontSize: 12, color: "#64748b" }}>Mostrando {desde} a {hasta} de {total} registros</span>
      <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
        <button onClick={() => onChange(pagina - 1)} disabled={pagina === 1}
          style={{ ...btnBase, color: pagina === 1 ? "#d1d5db" : "#475569", cursor: pagina === 1 ? "not-allowed" : "pointer" }}>
          Anterior
        </button>
        {nums.map((n, i) => n === "..." ? (
          <span key={`e${i}`} style={{ fontSize: 12, color: "#94a3b8", padding: "0 2px" }}>…</span>
        ) : (
          <button key={n} onClick={() => onChange(n)} style={{
            width: 32, height: 32, borderRadius: 6, border: "1px solid", fontSize: 12, fontWeight: 600, cursor: "pointer",
            borderColor: pagina === n ? "#1E3A5F" : "#e2e8f0",
            background:  pagina === n ? "#1E3A5F" : "#fff",
            color:       pagina === n ? "#fff"    : "#475569",
          }}>{n}</button>
        ))}
        <button onClick={() => onChange(pagina + 1)} disabled={pagina === totalPags}
          style={{ ...btnBase, color: pagina === totalPags ? "#d1d5db" : "#475569", cursor: pagina === totalPags ? "not-allowed" : "pointer" }}>
          Siguiente
        </button>
      </div>
    </div>
  );
}
